/**
 * ============================================================================
 * WITHDRAWAL HISTORY COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Withdrawal history display component
 */

import React, { useEffect, useState } from 'react';
import { useWallet } from '../../hooks/useWallet';
import useAuthStore from '../../store/authStore';
import { useI18n } from '../../providers';
import StatusBadge from '../common/StatusBadge';
import Spinner from '../common/Spinner';
import Modal from '../common/Modal';

/**
 * Withdrawal History Component
 * @returns {JSX.Element}
 */
const WithdrawalHistory = () => {
  const accessToken = useAuthStore((s) => s.accessToken);
  const { loadWithdrawals, cancelWithdrawal, withdrawals, loading } = useWallet();
  const { formatCurrency, formatDate, formatTime, language } = useI18n();
  const [selectedWithdrawal, setSelectedWithdrawal] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const list = Array.isArray(withdrawals) ? withdrawals : [];

  useEffect(() => {
    if (!accessToken) return;
    loadWithdrawals();
  }, [accessToken, loadWithdrawals]);

  if (loading && list.length === 0) {
    return (
      <div className="py-12 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div className="py-12 text-center">
        <div className="text-5xl mb-4 opacity-80">💸</div>
        <h3 className="dashboard-wt-card-title mb-2">
          {language === 'fr' ? 'Aucun retrait' : 'No withdrawals yet'}
        </h3>
        <p className="dashboard-wt-card-desc">
          {language === 'fr'
            ? 'Vos demandes de retrait apparaîtront ici.'
            : 'Your withdrawal requests will appear here.'}
        </p>
      </div>
    );
  }

  const getWithdrawalIcon = (method) => {
    const icons = {
      mtn_mobile_money: '📱',
      orange_money: '🟠',
      bank_transfer: '🏦',
      mpesa: '💚',
      airtel_money: '🔵',
    };
    return icons[method] || '💸';
  };

  const getStatusVariant = (status) => {
    const variants = {
      completed: 'success',
      processing: 'info',
      pending: 'warning',
      failed: 'error',
      cancelled: 'default',
    };
    return variants[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      pending: language === 'fr' ? 'En attente' : 'Pending',
      processing: language === 'fr' ? 'En cours' : 'Processing',
      completed: language === 'fr' ? 'Terminé' : 'Completed',
      failed: language === 'fr' ? 'Échoué' : 'Failed',
      cancelled: language === 'fr' ? 'Annulé' : 'Cancelled',
    };
    return labels[status] || status || '—';
  };

  const handleCancel = async () => {
    if (!selectedWithdrawal) return;
    try {
      await cancelWithdrawal(selectedWithdrawal.id, 'User requested cancellation');
      setShowCancelModal(false);
      setSelectedWithdrawal(null);
      loadWithdrawals();
    } catch (error) {
      console.error('Cancel withdrawal error:', error);
    }
  };

  return (
    <div className="wallet-wt-withdraw-list divide-y divide-[rgba(255,255,255,0.06)]">
      {list.map((withdrawal) => (
        <div
          key={withdrawal.id}
          className="wallet-wt-withdraw-history-item flex flex-wrap items-center gap-4"
        >
          <div className="w-11 h-11 rounded-xl bg-[rgba(34,197,94,0.12)] border border-[rgba(34,197,94,0.25)] flex items-center justify-center text-xl flex-shrink-0">
            {getWithdrawalIcon(withdrawal.withdrawalMethod || withdrawal.method)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-0.5">
              <span className="font-semibold text-[#fafafa]">
                {formatCurrency(withdrawal.amount || 0, withdrawal.currency || 'XAF')}
              </span>
              <StatusBadge variant={getStatusVariant(withdrawal.status)} size="sm">
                {getStatusLabel(withdrawal.status)}
              </StatusBadge>
            </div>
            <div className="flex flex-wrap items-center gap-x-2 text-sm text-[#a3a3a3]">
              <span>{withdrawal.withdrawalMethod || withdrawal.method || '—'}</span>
              {withdrawal.requestedAt && (
                <>
                  <span>·</span>
                  <span>{formatDate(withdrawal.requestedAt)}</span>
                </>
              )}
              {!withdrawal.requestedAt && withdrawal.createdAt && (
                <>
                  <span>·</span>
                  <span>{formatDate(withdrawal.createdAt)}</span>
                </>
              )}
            </div>
          </div>
          {withdrawal.status === 'pending' && (
            <button
              type="button"
              onClick={() => {
                setSelectedWithdrawal(withdrawal);
                setShowCancelModal(true);
              }}
              className="dashboard-wt-btn-secondary text-sm border-red-500/50 text-red-400 hover:bg-red-500/10"
            >
              {language === 'fr' ? 'Annuler' : 'Cancel'}
            </button>
          )}
        </div>
      ))}

      {/* Cancel confirmation modal */}
      <Modal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setSelectedWithdrawal(null);
        }}
        title={language === 'fr' ? 'Annuler le retrait' : 'Cancel Withdrawal'}
      >
        <div className="space-y-4">
          {selectedWithdrawal && (
            <p className="text-[#fafafa] font-semibold">
              {formatCurrency(selectedWithdrawal.amount || 0, selectedWithdrawal.currency || 'XAF')}
            </p>
          )}
          <p className="dashboard-wt-card-desc">
            {language === 'fr'
              ? 'Êtes-vous sûr de vouloir annuler cette demande de retrait ?'
              : 'Are you sure you want to cancel this withdrawal request?'}
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => {
                setShowCancelModal(false);
                setSelectedWithdrawal(null);
              }}
              className="dashboard-wt-btn-secondary flex-1"
            >
              {language === 'fr' ? 'Non' : 'No'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="flex-1 px-4 py-2.5 rounded-lg font-semibold border border-red-500/50 text-red-400 bg-red-500/10 hover:bg-red-500/20 transition-colors"
            >
              {language === 'fr' ? 'Oui, annuler' : 'Yes, cancel'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default WithdrawalHistory;
